import { useState, useEffect, useRef } from 'react';

const ONBOARDING_STORAGE_KEY = 'studybel-onboarding-completed';
const ONBOARDING_VERSION = '1.0'; // Increment this to show onboarding again for new features

interface OnboardingState {
  hasCompletedOnboarding: boolean;
  onboardingVersion: string;
}

interface UseOnboardingReturn {
  // State
  hasCompletedOnboarding: boolean;
  
  // Actions
  markOnboardingComplete: () => void;
  resetOnboarding: () => void; // For development/testing
  
  // Utilities
  shouldShowOnboarding: (userHasCourses: boolean) => boolean;
}

export const useOnboarding = (): UseOnboardingReturn => {
  const log = (...args: unknown[]) => console.debug('[Onboarding]', ...args);
  const [state, setState] = useState<OnboardingState>(() => {
    try {
      const stored = localStorage.getItem(ONBOARDING_STORAGE_KEY);
      if (stored) {
        log('Loaded stored onboarding state raw:', stored);
        const parsed = JSON.parse(stored);
        // Check if the stored version matches current version
        if (parsed.onboardingVersion === ONBOARDING_VERSION) {
          log('Hydrating onboarding state from storage', parsed);
          return {
            hasCompletedOnboarding: parsed.hasCompletedOnboarding || false,
            onboardingVersion: ONBOARDING_VERSION,
          };
        } else {
          log('Stored onboarding version mismatch', {
            storedVersion: parsed.onboardingVersion,
            expectedVersion: ONBOARDING_VERSION,
          });
        }
      } else {
        log('No stored onboarding state found');
      }
    } catch (error) {
      console.warn('[Onboarding] Failed to parse onboarding state from localStorage:', error);
    }

    // Default state for new users or version mismatch
    log('Falling back to default onboarding state');
    return {
      hasCompletedOnboarding: false,
      onboardingVersion: ONBOARDING_VERSION,
    };
  });

  const persistState = (stateToPersist: OnboardingState) => {
    try {
      localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify({
        hasCompletedOnboarding: stateToPersist.hasCompletedOnboarding,
        onboardingVersion: stateToPersist.onboardingVersion,
      }));
      log('Persisted onboarding state', stateToPersist);
    } catch (error) {
      console.warn('[Onboarding] Failed to save onboarding state to localStorage:', error);
    }
  };

  const skipNextPersistRef = useRef(false);

  // Persist onboarding state to localStorage
  useEffect(() => {
    if (skipNextPersistRef.current) {
      skipNextPersistRef.current = false;
      return;
    }
    persistState(state);
  }, [state.hasCompletedOnboarding, state.onboardingVersion]);

  const markOnboardingComplete = () => {
    const updatedState: OnboardingState = {
      ...state,
      hasCompletedOnboarding: true,
    };
    log('markOnboardingComplete called');
    persistState(updatedState);
    skipNextPersistRef.current = true;
    setState(updatedState);
  };

  const resetOnboarding = () => {
    skipNextPersistRef.current = true;
    setState({
      hasCompletedOnboarding: false,
      onboardingVersion: ONBOARDING_VERSION,
    });
    log('resetOnboarding called');
    try {
      localStorage.removeItem(ONBOARDING_STORAGE_KEY);
    } catch (error) {
      console.warn('[Onboarding] Failed to clear onboarding state from localStorage:', error);
    }
  };

  // Determine if onboarding should be shown based on user state
  const shouldShowOnboarding = (userHasCourses: boolean): boolean => {
    log('shouldShowOnboarding check', {
      hasCompletedOnboarding: state.hasCompletedOnboarding,
      userHasCourses,
    });
    // Don't show onboarding if:
    // 1. User has already completed it
    // 2. User already has courses (they're not new)
    if (state.hasCompletedOnboarding || userHasCourses) {
      return false;
    }

    // Show onboarding for new users with no courses
    return true;
  };

  return {
    // State
    hasCompletedOnboarding: state.hasCompletedOnboarding,
    
    // Actions
    markOnboardingComplete,
    resetOnboarding,
    
    // Utilities
    shouldShowOnboarding,
  };
};

// Helper function to check if development mode onboarding should be enabled
export const isDevelopmentOnboardingEnabled = (): boolean => {
  return process.env.NODE_ENV === 'development' && 
         localStorage.getItem('studybel-dev-onboarding') === 'enabled';
};

// Helper function to enable/disable development onboarding
export const setDevelopmentOnboarding = (enabled: boolean): void => {
  if (process.env.NODE_ENV === 'development') {
    if (enabled) {
      localStorage.setItem('studybel-dev-onboarding', 'enabled');
    } else {
      localStorage.removeItem('studybel-dev-onboarding');
    }
  }
}; 
